import React from 'react';
import { useNavigate } from 'react-router-dom';

import type { DateRangePresetLabel } from '@prerender/design-system/DateRangePicker';
import { type EmptyScopeState, type ScopePopulation, WIDER_PRESET_LABEL } from './EmptyScope';
import {
  FIRST_THIRTY_DAYS_HELP,
  HOW_PRERENDER_WORKS_HELP,
  INTEGRATION_PATH,
  INTEGRATION_WIZARD_PATH,
} from './EmptyStateHelp';
import LoadErrorEmptyState from './LoadErrorEmptyState';
import TrackedEmptyState from './TrackedEmptyState';

export type CrawlerTrafficScreen = 'dashboard' | 'cdn_analytics';

type ScreenCopy = {
  noun: string;
  notIntegrated: { headline: string; body: string; preview: string };
  loadErrorHeadline: string;
};

const SCREEN_COPY: Record<CrawlerTrafficScreen, ScreenCopy> = {
  dashboard: {
    noun: 'crawler activity',
    notIntegrated: {
      headline: 'Integrate your domain to see crawler traffic',
      body: 'See which crawlers visit, and what they get. We start collecting as soon as you integrate.',
      preview: '/img/empty-states/dashboard-preview.png',
    },
    loadErrorHeadline: "Couldn't load your dashboard",
  },
  cdn_analytics: {
    noun: 'crawler requests',
    notIntegrated: {
      headline: 'Integrate your domain to see CDN activity',
      body: 'See every crawler request we served, and how fast. We collect nothing until you integrate.',
      preview: '/img/empty-states/cdn-analytics-preview.png',
    },
    loadErrorHeadline: "Couldn't load CDN activity",
  },
};

const POPULATION_UNIT = 'crawler requests';
const WIDEN_DATE_RANGE_ACTION = {
  id: 'widen_date_range',
  label: `Show ${WIDER_PRESET_LABEL}`,
  destination: `date_range_${WIDER_PRESET_LABEL.replace(/ /g, '_')}`,
};

export type CrawlerTrafficEmptyStateProps = { screen: CrawlerTrafficScreen; compact?: boolean } & (
  | { kind: 'not-integrated' }
  | { kind: 'no-data' }
  | { kind: 'stalled'; windowLabel: DateRangePresetLabel }
  | { kind: 'filtered'; hiddenFilters?: string[]; population?: ScopePopulation; onClearFilters: () => void }
  | { kind: 'default-window-empty'; population?: ScopePopulation; onWidenDateRange: () => void }
  | { kind: 'error'; detail?: string; onRetry: () => void }
);

function withUnit(population: ScopePopulation | undefined) {
  return population && { ...population, unit: POPULATION_UNIT };
}

export function CrawlerTrafficEmptyState(props: CrawlerTrafficEmptyStateProps) {
  const navigate = useNavigate();
  const { screen, compact = false } = props;
  const copy = SCREEN_COPY[screen];
  const checkIntegration = {
    id: 'check_integration',
    label: 'Check your integration',
    destination: INTEGRATION_PATH,
    onClick: () => navigate(INTEGRATION_PATH),
  };

  switch (props.kind) {
    case 'not-integrated':
      return (
        <TrackedEmptyState
          screen={screen}
          state="not-integrated"
          headline={copy.notIntegrated.headline}
          body={copy.notIntegrated.body}
          action={{
            id: 'set_up_integration',
            label: 'Set up your integration',
            destination: INTEGRATION_WIZARD_PATH,
            onClick: () => navigate(INTEGRATION_WIZARD_PATH),
          }}
          secondaryAction={HOW_PRERENDER_WORKS_HELP}
          preview={copy.notIntegrated.preview}
          compact={compact}
        />
      );
    case 'no-data':
      return (
        <TrackedEmptyState
          screen={screen}
          state="no-data"
          headline={`No ${copy.noun} yet`}
          body="Crawlers decide when to visit. It can take a few days."
          action={checkIntegration}
          secondaryAction={compact ? undefined : FIRST_THIRTY_DAYS_HELP}
          compact={compact}
        />
      );
    case 'stalled':
      return (
        <TrackedEmptyState
          screen={screen}
          state="no-data"
          variant="stalled"
          headline={`No recent ${copy.noun}`}
          body="Crawlers visited before, but not recently. Check that your integration is still in place."
          action={checkIntegration}
          window={props.windowLabel}
          compact={compact}
        />
      );
    case 'filtered':
      return (
        <TrackedEmptyState
          screen={screen}
          state="filtered"
          variant="narrowed"
          headline={`No ${copy.noun} for these dates and filters`}
          body="Try a longer date range or fewer filters."
          hiddenFilters={props.hiddenFilters}
          population={withUnit(props.population)}
          action={{
            id: 'clear_filters',
            label: 'Clear filters',
            destination: 'filters_reset',
            onClick: props.onClearFilters,
          }}
          compact={compact}
        />
      );
    case 'default-window-empty':
      return (
        <TrackedEmptyState
          screen={screen}
          state="filtered"
          variant="default_window"
          headline={`No ${copy.noun} for these dates`}
          body="Try a longer date range."
          population={withUnit(props.population)}
          action={{ ...WIDEN_DATE_RANGE_ACTION, onClick: props.onWidenDateRange }}
          compact={compact}
        />
      );
    case 'error':
      return (
        <LoadErrorEmptyState
          screen={screen}
          headline={copy.loadErrorHeadline}
          detail={props.detail}
          onRetry={props.onRetry}
          compact={compact}
        />
      );
  }
}

type EmptyRangeStateProps = {
  screen: CrawlerTrafficScreen;
  scope: EmptyScopeState;
  onClearFilters: () => void;
  onWidenDateRange: () => void;
  compact?: boolean;
};

export function EmptyRangeState({ screen, scope, onClearFilters, onWidenDateRange, compact }: EmptyRangeStateProps) {
  switch (scope.kind) {
    case 'no-data':
      return <CrawlerTrafficEmptyState screen={screen} kind="no-data" compact={compact} />;
    case 'stalled':
      return (
        <CrawlerTrafficEmptyState screen={screen} kind="stalled" windowLabel={scope.windowLabel} compact={compact} />
      );
    case 'default-window-empty':
      return (
        <CrawlerTrafficEmptyState
          screen={screen}
          kind="default-window-empty"
          population={scope.population}
          onWidenDateRange={onWidenDateRange}
          compact={compact}
        />
      );
    case 'filtered':
      return (
        <CrawlerTrafficEmptyState
          screen={screen}
          kind="filtered"
          hiddenFilters={scope.hiddenFilters}
          population={scope.population}
          onClearFilters={onClearFilters}
          compact={compact}
        />
      );
  }
}
